// The mark a tenant gets when no logo is configured: its initials in a coloured
// tile, as the click dummy draws every corporate. Pure and deterministic, so
// the same school looks the same on every render, device and release.

// Words that carry no identity. "Lycée des Collines" is LC, not LD.
const PARTICLES = new Set([
  "de",
  "des",
  "du",
  "la",
  "le",
  "les",
  "l",
  "d",
  "et",
  "of",
  "the",
  "di",
  "da",
]);

const words = (name: string): string[] =>
  name
    .normalize("NFC")
    // A camel-case word is two words: SmartCash is SC, not SM.
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s\-_/.,]+/)
    .map(word => word.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(word => word !== "" && !PARTICLES.has(word.toLowerCase()));

// The first two initials of the name: one per word, or the first two letters
// when there is only one word. Never empty -- a mark with no letters would be
// a blank square.
export const initialsFor = (name: string): string => {
  const parts = words(name);
  if (parts.length === 0) return "?";

  const first = Array.from(parts[0])[0] ?? "";
  const second =
    parts.length > 1 ? Array.from(parts[1])[0] ?? "" : Array.from(parts[0])[1] ?? "";

  return `${first}${second}`.toUpperCase();
};

// FNV-1a. Chosen for spread, not security: initials that share a first letter
// still land far apart on the wheel, so two schools on one list do not look
// like the same brand.
const hash = (value: string): number => {
  let result = 0x811c9dc5;

  for (const char of value) {
    result ^= char.codePointAt(0) ?? 0;
    result = Math.imul(result, 0x01000193);
  }

  return result >>> 0;
};

const channel = (value: number): string =>
  Math.round(value).toString(16).padStart(2, "0").toUpperCase();

const hslToHex = (hue: number, saturation: number, lightness: number): string => {
  const s = saturation / 100;
  const l = lightness / 100;
  const k = (n: number) => (n + hue / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  return `#${channel(f(0) * 255)}${channel(f(8) * 255)}${channel(f(4) * 255)}`;
};

// A colour derived from the initials alone. The lightness is fixed low enough
// that white text on it clears the contrast threshold the palette uses (4.5),
// so a mark never needs a per-colour text decision.
export const markColour = (initials: string): string => {
  const hue = hash(initials.trim().toUpperCase()) % 360;

  return hslToHex(hue, 42, 30);
};
